// =============================================================================
// ui/state/useTradeRows.ts — read a date range, and derive it.
// -----------------------------------------------------------------------------
// Extracted from the Trades tab when the Dashboard needed the same thing. Two
// tabs each writing their own read-then-derive is how two screens come to show
// different numbers for the same day — and the derivation is exactly where that
// would happen silently, because a tab that forgot it renders the raw columns
// and looks fine (D59).
//
// ⚠ RETURNS NULL UNTIL EVERYTHING IT NEEDS HAS ARRIVED. The derivation depends on
// accounts (for the account's firm), the commission schedules (for the rate), the
// session timezone (for the day boundary) and the day-boundary mode (for which
// trades are in range at all). Deriving with any of them missing produces exactly
// the unhealed numbers this exists to fix — so a partial context is not "close
// enough", it is the bug wearing a hat.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useAccounts } from './accounts';
import { useCommissions } from './commissions';
import { usePreferences } from './preferences';
import { useSupabase } from './supabase';
import { useTradeFeed } from './tradeFeed';
import { loadTradesInRange, type TradeTableRow } from '../../io/sync/trades';
import { deriveRows } from '../../selectors/derive';
import type { Account } from '../../selectors/accounts';
import type { DayBoundaryMode, RangeBounds } from '../../selectors/range';
import { reportFailure } from '../../io/diagnostics/errorLog';

export type TradeRowsPhase = 'loading' | 'ready' | 'failed';

export interface TradeRowsResult {
  phase: TradeRowsPhase;
  /** ⚠ NULL until a read succeeded AND the derivation context is complete. */
  rows: readonly TradeTableRow[] | null;
  error: string | null;
  /**
   * 🚨 WHICH WINDOW `rows` ACTUALLY COVERS — which is NOT always the one the
   * caller just asked for.
   *
   * `rows` deliberately keeps the previous read's result while the next one is
   * in flight, so a tab does not blink to empty on every step. That is right for
   * a table, and WRONG for anything that prints a heading beside the numbers: a
   * tab that changes its own window — Review's four period buttons, the Day
   * Overview's arrows — renders one frame of the OLD period's figures under the
   * NEW period's title, and they read as that period's.
   *
   * ⚠ MEASURED on the running app, 2026-08-27: pressing `Monthly` on Review
   * showed the week's `+$249.11 · 4 trades` under the heading `August 2026`
   * before settling on the month's `+$316.85 · 19 trades`.
   *
   * ⚠ `phase === 'loading'` is NOT enough on its own. It is set inside the
   * effect, which runs AFTER the render that changed the window — so there is
   * always one frame where the phase still says `ready` over stale rows.
   * Comparing the window is what closes that frame.
   */
  loadedBounds: RangeBounds | null;
}

/**
 * @param bounds      null while the range is still resolving — no read is issued.
 * @param mode        null until preferences load — no read is issued.
 * @param reloadNonce bump to force a re-read after a WRITE.
 *
 * ⚠ The nonce exists because the import writes straight to the database, so
 * nothing else would tell a tab its data changed — and a table still showing the
 * old count after a successful import reads as a failed import.
 *
 * ⚠ THERE IS A SECOND NONCE AND IT IS NOT A PARAMETER (TCR-95). `reloadNonce`
 * covers writes THIS CLIENT made. Trades pushed from ATAS are written by
 * something else entirely, so nothing in this process knows they arrived —
 * `state/tradeFeed.tsx` polls for them and bumps a shared nonce, which is read
 * out of context here rather than threaded through eleven call sites. Outside
 * that provider it is 0 forever, which is the behaviour every existing caller
 * and test already has.
 */
export function useTradeRows(
  bounds: RangeBounds | null,
  mode: DayBoundaryMode | null,
  reloadNonce = 0,
): TradeRowsResult {
  const { sb } = useSupabase();
  const { accounts } = useAccounts();
  const { schedules } = useCommissions();
  const { prefs } = usePreferences();
  const { nonce: feedNonce } = useTradeFeed();

  const [phase, setPhase] = useState<TradeRowsPhase>('loading');
  const [error, setError] = useState<string | null>(null);
  // ⚠ ONE piece of state, holding the rows AND the window they came from. Two
  // separate `useState`s would be two claims that can disagree for a render —
  // and "which window are these rows" is exactly what a caller printing a
  // heading beside them has to be able to trust.
  const [loaded, setLoaded] =
    useState<{ raw: readonly TradeTableRow[]; bounds: RangeBounds } | null>(null);

  // ⚠ Keyed on the VALUES, not the object. `bounds` is a fresh object whenever
  // the range memo recomputes, and depending on the reference re-fires the read
  // on unrelated renders — a request per keystroke in the custom-date field.
  const key = bounds ? `${bounds.startDate ?? ''}|${bounds.endDate ?? ''}` : '';

  useEffect(() => {
    // ⚠ `prefs` IS PART OF THE GUARD NOW, not just the derive step: the filter
    // computes the session day and needs the SAME timezone `deriveRow` uses.
    // Reading with a default while deriving with the setting would put a trade
    // in a day and leave it out of that day's totals (TCR-96).
    if (!bounds || !mode || !prefs) return;

    let cancelled = false;
    setPhase('loading');
    setError(null);

    void (async () => {
      try {
        const res = await loadTradesInRange(sb, bounds, mode, prefs.sessionTimezone);
        if (cancelled) return;
        setLoaded({ raw: res.rows, bounds });
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        // ⚠ `loaded` keeps whatever it had — never []. An empty result is the
        // "no trades in this range" screen, and showing that because a request
        // failed tells a user their data is gone.
        setError(reportFailure('trades', e));
        setPhase('failed');
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sb, key, mode, reloadNonce, feedNonce, prefs]);

  const rows = useMemo(() => {
    if (!loaded || !accounts || !schedules || !prefs) return null;
    return deriveRows(loaded.raw, {
      accounts: accounts as readonly Account[],
      schedules,
      sessionTz: prefs.sessionTimezone,
    });
  }, [loaded, accounts, schedules, prefs]);

  return { phase, rows, error, loadedBounds: loaded?.bounds ?? null };
}
